import { ObjectId } from "@/model/db/General";
import { TranslatedLabel } from "@/services/LanguageService";
import BaseAsset, {
  ConfigBaseAsset,
  ConfigBaseAssetData,
  TypedBaseAsset,
} from "../../../model/general-assets/BaseAsset";
import { MatchQuery } from "../../../services/DataService";
import {
  InvoiceAccountDataCategory,
  InvoiceDirection,
} from "../invoiceApp/RAInterfaces";
import { Discount } from "../invoiceApp/components/InvoiceForm/RAInvoiceFormModel";

export interface InvoiceApprovalChain extends BaseAsset {
  data: {
    type: string;
    displayName: string;
    version?: number;
    versionData: {
      rules: InvoiceApprovalChainData[];
    };
    status: "active" | "archived";
    //deprecated stuff - should be replaced
    prefilter?: any;
    chainElements?: any;
  };
}
export type InvoiceApprovalChainData = {
  valueBoundary: number;
  switchFilter: {
    matchQuery: MatchQuery;
    value: {
      users: string[];
      teams: string[];
    };
  }[];
  default: {
    users: string[];
    teams: string[];
  };
};

export interface RACInvoiceType extends ConfigBaseAsset {
  data: ConfigBaseAssetData & {
    chain: string;
    relevantActivity?: string[];
    direction: InvoiceDirection;
  };
}

export interface RACInvoiceTypeCostCenter extends ConfigBaseAsset {
  data: ConfigBaseAssetData & {
    parentInvoiceType: string;
    lqCategory: string;
  };
}
export type PaymentSEPA = {
  kind: "sepa";
};
export type PaymentBankTransfer = {
  kind: "bankTransfer";
  payments: {
    ratioOfPayment: number; // 0-100
    daysForPayment: number;
    discounts?: Discount[];
  }[];
};
export type PaymentPaypal = {
  kind: "paypal";
};
export type PaymentCheck = {
  kind: "check";
};
export type PaymentCreditCard = {
  kind: "creditCard";
};
// export type PaymentCashInAdvance = {
//   kind: "cashInAdvance";
// };
export type PaymentCash = {
  kind: "cash";
};
export type PaymentSettlement = {
  kind: "settlement";
};
export type PaymentOther = {
  kind: "other";
};
export type PaymentType =
  | PaymentSEPA
  | PaymentBankTransfer
  | PaymentPaypal
  // | PaymentCashInAdvance
  | PaymentCheck
  | PaymentSettlement
  | PaymentCreditCard
  | PaymentCash
  | PaymentOther;

/**
 * i18n.t("ra:Invoice.PaymentKind.sepa", "Lastschrift (SEPA)"),
 * i18n.t("ra:Invoice.PaymentKind.ibanNational", "Überweisung (Inland)"),
 * i18n.t("ra:Invoice.PaymentKind.ibanInternational", "Überweisung (EU)"),
 * i18n.t("ra:Invoice.PaymentKind.international", "Überweisung (international)"),
 * i18n.t("ra:Invoice.PaymentKind.paypal", "PayPal"),
 * i18n.t("ra:Invoice.PaymentKind.check", "Scheck"),
 * i18n.t("ra:Invoice.PaymentKind.creditCard", "Kreditkarte"),
 * i18n.t("ra:Invoice.PaymentKind.cashInAdvance", "Vorkasse"),
 * i18n.t("ra:Invoice.PaymentKind.cash", "Bar"),
 * i18n.t("ra:Invoice.PaymentKind.settlement", "Verrechnung"),
 * i18n.t("ra:Invoice.PaymentKind.other", "Sonstiges"),
 */
export enum PaymentKind {
  SEPA = "sepa",
  BANK_TRANSFER = "bankTransfer",
  PAYPAL = "paypal",
  CHECK = "check",
  CREDIT_CARD = "creditCard",
  CASH_IN_ADVANCE = "cashInAdvance",
  CASH = "cash",
  SETTLEMENT = "settlement",
  OTHER = "other",
}

export type UpdateInvoiceDeclineReasonAssignment = {
  type: "user" | "team" | "previousAssignee";
  id?: ObjectId[];
};

export class UpdateInvoiceDeclineReasonTodo {
  type: DeclineReasonActionType;
  value?: string;
  id: ObjectId;
}

/**
 * i18n.t("ra:Invoice.DeclineReasonActionType.needCorrection", "Rechnungskorrektur erforderlich"),
 * i18n.t("ra:Invoice.DeclineReasonActionType.needCorrectionDescription", "Der Bearbeiter muss eine Rechnungskorrektur anforderung und hochladen"),
 * i18n.t("ra:Invoice.DeclineReasonActionType.needCorrectionTodo", "Fordern Sie eine Rechnungskorrektur an und laden Sie sie hoch"),
 * i18n.t("ra:Invoice.DeclineReasonActionType.needStorno", "Stornorechnung erforderlich"),
 * i18n.t("ra:Invoice.DeclineReasonActionType.needStornoDescription", "Der Bearbeiter muss eine Stornorechnung anforderung und hochladen"),
 * i18n.t("ra:Invoice.DeclineReasonActionType.needStornoTodo", "Fordern Sie eine Stornorechnung an und laden Sie sie hoch"),
 * i18n.t("ra:Invoice.DeclineReasonActionType.checkRefund", "Rückzahlung muss geprüft werden"),
 * i18n.t("ra:Invoice.DeclineReasonActionType.checkRefundDescription", "Der Bearbeiter muss prüfen, ob eine Rückzahlung erforderlich ist und diese beantragen"),
 * i18n.t("ra:Invoice.DeclineReasonActionType.checkRefundTodo", "Überprüfen Sie, ob eine Rückzahlung erforderlich ist und beantragen Sie diese"),
 * i18n.t("ra:Invoice.DeclineReasonActionType.manualTask", "Manuelle Aufgabe"),
 */
export enum DeclineReasonActionType {
  NEED_CORRECTION = "needCorrection",
  NEED_STORNO = "needStorno",
  CHECK_REFUND = "checkRefund",
  MANUAL_TASK = "manualTask",
}

export type DeclineReasonConfig = {
  // type: string;
  id: ObjectId;
  displayName: TranslatedLabel;
  info: TranslatedLabel;
  assignment: UpdateInvoiceDeclineReasonAssignment[];
  todos: UpdateInvoiceDeclineReasonTodo[];
  status: "active" | "archived";
};
export type DeclineReasonConfigWithType = DeclineReasonConfig & {
  type: string;
};

export type DeclineReasonConfigFormValue = {
  type: string;
  id: ObjectId;
  displayName: TranslatedLabel;
  info: TranslatedLabel;
  assignments: {
    users: { active: boolean; ids: ObjectId[] };
    teams: { active: boolean; ids: ObjectId[] };
    assignees: { active: boolean };
  };
  todos: UpdateInvoiceDeclineReasonTodo[];
  status: "active" | "archived";
};

export type ModeOfPayment = {
  id: string;
  identifier: string;
  displayName: string;
  status: "active" | "archived";
  editable: boolean;
} & PaymentType;

export interface InvoicePaymentType {
  id: ObjectId;
  status: "active" | string;
  displayName: string;
  tags: InvoicePaymentTypeTags[];
}

export interface InvoiceCheckChainRules {
  valueBoundary: number;
  switchFilter: {
    matchQuery: MatchQuery;
    value: {
      user: string;
      team: string;
    };
  }[];
  default: {
    user: string;
    team: string;
  };
}
export interface InvoiceApprovalChainRules {
  valueBoundary: number;
  switchFilter: {
    matchQuery: MatchQuery;
    value: {
      users: string[];
      teams: string[];
    };
  }[];
  default: {
    users: string[];
    teams: string[];
  };
}
export type InvoiceAccountingRule = {
  cost2?: string;
  cost1?: string;
  referenceField?: string;
  extraInfo?: (string | null)[];
  usage?: string;
};
export interface InvoiceConfig extends BaseAsset {
  _id: ObjectId;
  data: {
    type: string;
    customFields: {
      id: ObjectId;
      displayName: TranslatedLabel;
    }[];
    bookingEnd?: {
      endDate: Date;
      entity: ObjectId;
    }[];
    checkList: InvoiceCheckChainRules[];
    paymentTypes: InvoicePaymentType[]; // deprecated
    outgoingPaymentMode: ModeOfPayment[];
    incomingPaymentMode: ModeOfPayment[];
    incomingDeclineReason: DeclineReasonConfig[];
    outgoingDeclineReason: DeclineReasonConfig[];

    incomingAccountingRule?: InvoiceAccountingRule;
    outgoingAccountingRule?: InvoiceAccountingRule;
  };
}
export type ACInvoiceStructConfig = InvoiceConfig;

/**
 * i18n.t("ra:Invoice.TypeOfBankTransfer.PAYMENT_IBAN", "IBAN National"),
 * i18n.t("ra:Invoice.TypeOfBankTransfer.PAYMENT_INTERNATIONAL", "International"),
 * i18n.t("ra:Invoice.TypeOfBankTransfer.PAYMENT_IBAN_INTERNATIONAL", "IBAN International"),
 */
export enum TypeOfBankTransfer {
  "PAYMENT_IBAN" = "PAYMENT_IBAN",
  "PAYMENT_IBAN_INTERNATIONAL" = "PAYMENT_IBAN_INTERNATIONAL",
  "PAYMENT_INTERNATIONAL" = "PAYMENT_INTERNATIONAL",
}

export enum InvoicePaymentTypeTags {
  "SHOULD_BE_BOOKED" = "SHOULD_BE_BOOKED",
  "SHOULD_BE_PAYED" = "SHOULD_BE_PAYED",
  "SHOULD_BE_COMPENSATED" = "SHOULD_BE_COMPENSATED",
  "SHOULD_BE_CHECKED" = "SHOULD_BE_CHECKED",

  "IS_STORNO" = "IS_STORNO",

  "PAYMENT_IBAN" = "PAYMENT_IBAN",
  "PAYMENT_US_NATIONAL" = "PAYMENT_US_NATIONAL",
  "PAYMENT_US_INTERNATIONAL" = "PAYMENT_US_INTERNATIONAL",
  // "PAYMENT_CASH" = "PAYMENT_CASH",
}

export interface ACInvoiceStruct {
  invoiceConfig: ACInvoiceStructConfig[];
  invoiceTypes: ACInvoiceType[];
}
export interface ACInvoiceStructByUnit {
  invoiceConfig: ACInvoiceStructConfig;
  invoiceTypes: ACInvoiceType[];
}
export type ACCostCenter = {
  costCenterId: string;
  displayName: TranslatedLabel;
  description?: TranslatedLabel;
};

export interface ACInvoiceType {
  _id: string;
  data: {
    type: string;
    status: "active";
    direction: InvoiceDirection;
    displayName: TranslatedLabel;
    chain: ACInvoiceChain;
    relevantActivity?: string[];
    costCenters: ACCostCenter[];
    sortIndex: number;
    description?: TranslatedLabel;
  };
}
export interface ACInvoiceTypePrefilter {
  filter: MatchQuery;
  chainElements: ACInvoiceChain;
}

export interface ACInvoiceChain {
  _id: string;
  displayName: string;
  prefilter?: ACInvoiceTypePrefilter[];
}

export type RACImportValueEntry = {
  accountNumber: string;
  defaultBU?: ObjectId;
  description: string;
  detailedDescription: string;
  category: InvoiceAccountDataCategory;
};
export type RACImportFileEntry = {
  accountNumber: string;
  defaultBU?: string;
  description: string;
  detailedDescription: string;
};

export type RACImportBUValueEntry = {
  accountNumber: string;
  description: string;
  ust: string;
};

export type RACAccountBUImportFormData = {
  entity: string;
  data: RACImportValueEntry[];
};

export type RACAccountImportFormData = {
  entity: string;
  data: RACImportValueEntry[];
};

export type RACBookingReasonData = {
  type: string;
  entityId: ObjectId;
  reasonType: "overbook" | "underbook";
  name: string;
  description?: string;
  status: "active" | "archived";
  accountId: ObjectId;
  accountBUId?: ObjectId;
  direction: InvoiceDirection;
};
export type RACBookingReason = TypedBaseAsset<RACBookingReasonData>;
